import { instance, setHeader } from "./auth-api";
import { accountAPI } from "./account-api";

/**
 * Pay-out API.
 *
 * Backend contract (accountio-ws branch feature/payouts, Oct 2026):
 *
 *   GET  /application/:applicationID/payout-settings
 *          -> { success, settingsList: [{ _id, type, data, createdAt, updatedAt }] }
 *   POST /application/:applicationID/payout-settings
 *          { type: 'BANK' | 'WISE_EMAIL' | 'WISE_TAG', data: { ...fields } }
 *          -> { success, settings }
 *   PUT  /application/:applicationID/payout-settings/:settingsID
 *          same body -> { success, settings }
 *   403 "Cannot update payout settings while there are open payouts" when a
 *   pay-out wire is still pending/claimed — the UI shows the message.
 *
 *   Pay-out request = a wire of type "payout" through the existing wire route:
 *   POST /wire { applicationID, type: 'payout', amount, payoutData: { payoutSettingsID }, source }
 *          -> { success, wire }   (status starts as "pending"; amount in amount.pending)
 *   403 "Cannot create payout while there are open payouts".
 *   A trader may only move a pay-out from pending to cancelled.
 *
 *   Automatic payout lives on the application document and is saved through
 *   the regular POST /application (clientID + autoPayoutSettings):
 *          autoPayoutSettings: { enabled, payoutSettingsID, minimumPerformanceBond }
 *
 *   Required data fields (constants/wire.js PAYOUT_REQUIRED_FIELDS_TO_TYPE):
 *     BANK: beneficiaryName, bankName, accountType, country, city, street, state,
 *           postCode, routingNumber, accountNumber, birthDate, birthPlace
 *           beneficiaryEmail (added on dev). The portal also sends `beneficiaryType`
 *           (Individual | Business) — TODO(backend): add it to the BANK template
 *     WISE_EMAIL: wiseEmail     WISE_TAG: wiseTag
 *
 * Available for pay-out = PayDue from the latest "summary/balance" extension
 * (GET /application/ext/:id), the same data the Account Overview reads.
 * The backend does not validate requests against it yet.
 *
 * Still missing on the backend:
 *   - deleting a payout setting (no DELETE route; delete icon hidden)
 *
 * The UI works with a normalised shape:
 *   paymentMethods: { bankAccount }   = { _id, type, ...data } | null
 *   autoPayout:     { enabled, minimumPB, paymentMethod }  (paymentMethod = payout settings _id)
 *
 * Set REACT_APP_PAYOUT_MOCK=true to run the UI against an in-browser mock
 * (persisted in localStorage) without the backend.
 */

// Product decision (Oct 2026): only one payment method, the bank account.
// The backend also knows WISE_EMAIL / WISE_TAG types; they are ignored here.
export const PAYMENT_METHOD_KINDS = {
  bankAccount: "bankAccount",
};

export const PAYMENT_METHOD_LABELS = {
  [PAYMENT_METHOD_KINDS.bankAccount]: "Bank account",
};

// UI kind <-> backend type
export const KIND_TO_TYPE = {
  [PAYMENT_METHOD_KINDS.bankAccount]: "BANK",
};
const TYPE_TO_KIND = Object.fromEntries(
  Object.entries(KIND_TO_TYPE).map(([kind, type]) => [type, kind])
);

const SOURCE = "Trader's portal";
const isMock = process.env.REACT_APP_PAYOUT_MOCK === "true";

const ok = (data) => ({ status: 200, data: { success: true, ...data } });

/** Backend list -> { bankAccount } (one record per kind; other types ignored). */
export const normalizePaymentMethods = (list = []) => {
  const result = { bankAccount: null };
  (Array.isArray(list) ? list : []).forEach((item) => {
    const kind = TYPE_TO_KIND[item?.type];
    if (!kind) return;
    result[kind] = { _id: item._id, type: item.type, ...(item.data || {}) };
  });
  return result;
};

/** Application.autoPayoutSettings -> UI autoPayout */
export const normalizeAutoPayout = (autoPayoutSettings) => ({
  enabled: !!autoPayoutSettings?.enabled,
  minimumPB:
    typeof autoPayoutSettings?.minimumPerformanceBond === "number"
      ? autoPayoutSettings.minimumPerformanceBond
      : null,
  paymentMethod: autoPayoutSettings?.payoutSettingsID || null,
});

/** UI autoPayout -> Application.autoPayoutSettings */
export const denormalizeAutoPayout = (autoPayout) => ({
  enabled: !!autoPayout?.enabled,
  payoutSettingsID: autoPayout?.paymentMethod || null,
  minimumPerformanceBond:
    typeof autoPayout?.minimumPB === "number" ? autoPayout.minimumPB : undefined,
});

const settingsUrl = (accID) => `/application/${accID}/payout-settings`;

/** PayDue of the most recent "summary/balance" extension, or undefined. */
export const getPayDueFromExtensions = (extensions) => {
  const balances = (Array.isArray(extensions) ? extensions : []).filter(
    (e) => e?.code === "summary/balance" && e?.ext
  );
  if (!balances.length) return undefined;
  const latest = balances.reduce((a, b) =>
    new Date(b.date || 0) > new Date(a.date || 0) ? b : a
  );
  const value = Number(latest.ext.PayDue);
  return Number.isFinite(value) ? Math.max(0, value) : undefined;
};

// ---------------------------------------------------------------------------
// Real API
// ---------------------------------------------------------------------------
const realAPI = {
  supportsDelete: false,

  /**
   * Auto-payout comes from the application document; the account is passed in
   * by the thunk so no extra request is needed.
   */
  getPayoutSettings: async (accID, account, extensions) =>
    ok({
      settings: {
        autoPayout: normalizeAutoPayout(account?.autoPayoutSettings),
        availableForPayout: getPayDueFromExtensions(extensions),
      },
    }),

  updatePayoutSettings: async (accID, autoPayout) => {
    const autoPayoutSettings = denormalizeAutoPayout(autoPayout);
    const response = await accountAPI.updateAccountInfo(accID, { autoPayoutSettings });
    return {
      ...response,
      data: {
        ...(response.data || {}),
        success: response.status === 200,
        autoPayoutSettings,
        settings: { autoPayout: normalizeAutoPayout(autoPayoutSettings) },
      },
    };
  },

  getPaymentMethods: async (accID) => {
    const response = await instance.get(settingsUrl(accID), { headers: setHeader() });
    return ok({ items: normalizePaymentMethods(response.data?.settingsList) });
  },

  savePaymentMethod: async (accID, kind, data, settingsID) => {
    const body = { type: KIND_TO_TYPE[kind], data };
    if (settingsID) {
      await instance.put(`${settingsUrl(accID)}/${settingsID}`, body, { headers: setHeader() });
    } else {
      await instance.post(settingsUrl(accID), body, { headers: setHeader() });
    }
    return realAPI.getPaymentMethods(accID);
  },

  deletePaymentMethod: async () => {
    // No DELETE /payout-settings/:id route on the backend
    throw new Error("Removing a payment method is not supported yet");
  },

  createPayout: async (accID, { amount, payoutSettingsID }) => {
    const response = await instance.post(
      "wire",
      {
        applicationID: accID,
        type: "payout",
        amount,
        payoutData: { payoutSettingsID },
        source: SOURCE,
      },
      { headers: setHeader() }
    );
    return response;
  },

  getPayoutWires: async () => ok({ items: [] }),
};

// ---------------------------------------------------------------------------
// Mock API (REACT_APP_PAYOUT_MOCK=true)
// ---------------------------------------------------------------------------
const storageKey = (accID) => `cabinet.payoutMock.${accID}`;

const defaultMockState = () => ({
  autoPayoutSettings: { enabled: false, payoutSettingsID: null, minimumPerformanceBond: 1000 },
  availableForPayout: 9522,
  payoutSettings: [], // backend-shaped list: [{ _id, type, data }]
  payouts: [],
});

const readMock = (accID) => {
  try {
    const raw = window.localStorage.getItem(storageKey(accID));
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && parsed.payoutSettings ? parsed : defaultMockState();
  } catch (e) {
    return defaultMockState();
  }
};

const writeMock = (accID, state) => {
  try {
    window.localStorage.setItem(storageKey(accID), JSON.stringify(state));
  } catch (e) {
    // ignore
  }
  return state;
};

const delayed = (data) =>
  new Promise((resolve) => setTimeout(() => resolve(ok(data)), 250));

const mockSettings = (state) => ({
  autoPayout: normalizeAutoPayout(state.autoPayoutSettings),
  availableForPayout: state.availableForPayout,
});

const mockAPI = {
  supportsDelete: true,
  getPayoutSettings: (accID) => delayed({ settings: mockSettings(readMock(accID)) }),
  updatePayoutSettings: (accID, autoPayout) => {
    const state = readMock(accID);
    state.autoPayoutSettings = { ...state.autoPayoutSettings, ...denormalizeAutoPayout(autoPayout) };
    writeMock(accID, state);
    return delayed({
      settings: mockSettings(state),
      autoPayoutSettings: state.autoPayoutSettings,
    });
  },
  getPaymentMethods: (accID) =>
    delayed({ items: normalizePaymentMethods(readMock(accID).payoutSettings) }),
  savePaymentMethod: (accID, kind, data, settingsID) => {
    const state = readMock(accID);
    const hasOpenPayouts = state.payouts.some((p) => p.status === "claimed" || p.status === "pending");
    if (hasOpenPayouts) {
      const error = new Error("Cannot update payout settings while there are open payouts");
      error.response = { status: 403, data: { message: error.message } };
      return Promise.reject(error);
    }
    const type = KIND_TO_TYPE[kind];
    const existing = state.payoutSettings.find((s) => s._id === settingsID || s.type === type);
    if (existing) {
      existing.data = data;
    } else {
      state.payoutSettings.push({ _id: `mock-${kind}-${Date.now()}`, type, data });
    }
    writeMock(accID, state);
    return delayed({ items: normalizePaymentMethods(state.payoutSettings) });
  },
  deletePaymentMethod: (accID, kind) => {
    const state = readMock(accID);
    const type = KIND_TO_TYPE[kind];
    const removed = state.payoutSettings.find((s) => s.type === type);
    state.payoutSettings = state.payoutSettings.filter((s) => s.type !== type);
    if (removed && state.autoPayoutSettings.payoutSettingsID === removed._id) {
      state.autoPayoutSettings = { ...state.autoPayoutSettings, enabled: false, payoutSettingsID: null };
    }
    writeMock(accID, state);
    return delayed({ items: normalizePaymentMethods(state.payoutSettings) });
  },
  createPayout: (accID, { amount, payoutSettingsID }) => {
    const state = readMock(accID);
    if (state.payouts.some((p) => p.status === "claimed" || p.status === "pending")) {
      const error = new Error("Cannot create payout while there are open payouts");
      error.response = { status: 403, data: { message: error.message } };
      return Promise.reject(error);
    }
    const wire = {
      _id: `mock-payout-${Date.now()}`,
      applicationID: accID,
      type: "payout",
      status: "pending",
      amount: { pending: amount },
      payoutData: { payoutSettingsID },
      requestedOn: new Date().toISOString(),
    };
    state.payouts.unshift(wire);
    state.availableForPayout = Math.max(0, state.availableForPayout - amount);
    writeMock(accID, state);
    return delayed({ wire });
  },
  getPayoutWires: (accID) => delayed({ items: readMock(accID).payouts }),
};

export const payoutAPI = {
  isMock,
  ...(isMock ? mockAPI : realAPI),
};
